import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Download, Thermometer, TrendingUp, TrendingDown, Users, AlertTriangle, Leaf, DollarSign, Scale, Sparkles } from 'lucide-react';

const RiskAssessment = () => {
  const riskData = [
    { category: "Climate Risk", score: 65, trend: 2, icon: Thermometer },
    { category: "Social Risk", score: 48, trend: -3, icon: Users },
    { category: "Governance Risk", score: 55, trend: 1, icon: AlertTriangle },
    { category: "Environmental Risk", score: 70, trend: 3, icon: Leaf },
    { category: "Financial Risk", score: 40, trend: -1, icon: DollarSign },
    { category: "Regulatory Risk", score: 58, trend: 2, icon: Scale },
  ];

  const getRiskLevel = (score) => {
    if (score > 70) return "High";
    if (score > 50) return "Medium";
    return "Low";
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">ESG Risk Assessment</h1>
        <Button><Download className="mr-2 h-4 w-4" /> Export Report</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {riskData.map((risk, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                {risk.category}
                {React.createElement(risk.icon, { className: "h-4 w-4 text-gray-400" })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold mb-2">{risk.score}/100</p>
              <p className="text-sm mb-1">Risk level: {getRiskLevel(risk.score)}</p>
              <div className="flex items-center">
                {risk.trend > 0 ? (
                  <TrendingUp className="h-4 w-4 text-red-500 mr-1" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-green-500 mr-1" />
                )}
                <span className={risk.trend > 0 ? "text-red-500" : "text-green-500"}>
                  {Math.abs(risk.trend)}% from last assessment
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>ESG Risk Scores Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={riskData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>ESG Risk Radar</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={riskData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="category" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar name="Risk Score" dataKey="score" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
  <CardHeader>
    <CardTitle className="flex items-center">
      <Sparkles className="mr-2 h-5 w-5 text-yellow-500" />
      AI-Powered Risk Mitigation Recommendations
    </CardTitle>
  </CardHeader>
  <CardContent>
    <p className="mb-3 text-sm text-gray-600 italic">
      Our AI has analyzed your risk profile and suggests the following mitigation strategies:
    </p>
    <ul className="list-disc pl-5 space-y-2">
      <li>Implement robust climate change adaptation strategies to address high climate risk.</li>
      <li>Enhance diversity and inclusion programs to mitigate social risks.</li>
      <li>Strengthen board oversight and transparency to improve governance risk score.</li>
      <li>Invest in sustainable technologies to reduce environmental risks.</li>
      <li>Develop comprehensive financial risk management strategies.</li>
      <li>Stay proactive in monitoring and adapting to regulatory changes.</li>
    </ul>
  </CardContent>
</Card>

    </div>
  );
};

export default RiskAssessment;